import { 
  Firestore, 
  doc, 
  getDoc, 
  serverTimestamp,
  DocumentReference
} from "firebase/firestore";
import { Offer } from "@/types/offer";
import { CampLocation } from "@/types/campLocation";
import { ensureValidDate } from "@/utils/dateUtils";
import { prepareOfferDataCore, storedSessionAgeInputs, normalizeAgeUiSentinelsKeepingStoredShape, DecimalEnumItem } from "@/functions-shared/offer/prepareOfferDataCore";
import { shouldForceHideFromWorkflowStatus } from "@/utils/workflowForceHide";
import { resolveCampSlugPathForOffer } from "@/utils/resolveCampSlugPathForOffer";
import { resolveOrgIdFromProvidedByOrg } from "@/functions-shared/resolveOrgIdFromProvidedByOrg";
import { normalizeVariationDataAgeUiSentinels } from "@/utils/normalizeVariationDataSentinels";
import { sanitizeAuthoredRegUrl } from "@/functions-shared/offer/regUrlValidation";
import { offerProgramUrlFields } from "@/functions-shared/offer/sessionLinkDefaults";
import { deriveOfferVisibility } from "@/functions-shared/offer/deriveOfferVisibility";
import { applyStoredSessionPrice, campDataCatalogInputs, isCampDataOffer, type RebuildWrite } from "@/functions-shared/offer/storedSessionPrice";
import { applyStoredSessionWindow } from "@/functions-shared/offer/storedSessionWindow";

// Helper function to prepare full offer data with all required fields
export const prepareOfferData = async (
  db: Firestore,
  locationId: string,
  offerData: Partial<Offer>,
  userId: string,
  /**
   * KB-3733 — the STORED offer this save rebuilds, as it was BEFORE the edit (the edit arrives in
   * `offerData`), or `null` for a create or copy. Pre-edit matters: the dialog re-derives `priceRegular`
   * from the catalog on every save (`prepareSubmissionValues`), so a merged doc would hand the catalog
   * price in as the session's observed price. A camp-data session keeps its own price, tiers and length instead of
   * re-resolving them from the catalog variation (`applyStoredSessionPrice`, shared with the server
   * seam). Required, so the compiler lists every caller rather than a reviewer.
   */
  storedOffer: Record<string, unknown> | null
): Promise<any> => {
  console.log(`Preparing offer data for location: ${locationId} with variation: ${offerData.variationKey}, userId: ${userId}`);
  if (!locationId) throw new Error('Location ID is required');
  if (!userId) throw new Error('User ID is required');

  // Get location document
  const locationRef = doc(db, "campLocations", locationId);
  const locationSnap = await getDoc(locationRef);
  if (!locationSnap.exists()) throw new Error(`Location ${locationId} not found`);
  const locationData = locationSnap.data() as CampLocation;

  // Resolve orgName from the source of truth: campOrganizations/<orgId>.name via campLocations.providedByOrg
  let resolvedOrgName = "";
  try {
    const orgId = resolveOrgIdFromProvidedByOrg((locationData as any)?.providedByOrg);
    if (orgId) {
      const orgSnap = await getDoc(doc(db, "campOrganizations", orgId));
      const orgData: any = orgSnap.exists() ? orgSnap.data() : null;
      resolvedOrgName = typeof orgData?.name === "string" ? orgData.name : "";
    }
  } catch {
    resolvedOrgName = "";
  }
  // Back-compat: if location has a legacy orgName field, use it only when org doc lookup failed
  if (!resolvedOrgName) {
    const legacy = (locationData as any)?.orgName;
    resolvedOrgName = typeof legacy === "string" ? legacy : "";
  }

  // Variation
  const variation = locationData.variations?.find(v => v.key === offerData.variationKey);
  if (!variation) {
    const error = new Error(`Variation ${offerData.variationKey} not found in location ${locationId}`);
    (error as any).missingVariationKey = offerData.variationKey;
    (error as any).locationId = locationId;
    (error as any).latestVersionId = locationData.latestVersionId;
    throw error;
  }

  // Load decimal enums used for increment value matching (robust to test mocks)
  // Individual enum fetch (optional/no-op if missing)
  try {
    await getDoc(doc(db, "decimalEnums", variation.regularOfferIncrementKey || "__noop__"));
  } catch (ignore) {
    // Optional read to warm caches; safe to ignore failures
  }
  const decimalEnums: Record<string, DecimalEnumItem> = {} as any;
  try {
    const firestore = await import("firebase/firestore");
    const enumsQuerySnap: any = await firestore.getDocs(firestore.collection(db as any, "decimalEnums") as any);
    if (enumsQuerySnap && typeof enumsQuerySnap.forEach === 'function') {
      enumsQuerySnap.forEach((d: any) => {
        const data = d.data ? d.data() : d;
        const id = d.id ?? data?.id;
        if (!id) return;
        decimalEnums[id] = { id, value: data.value, shortLabel: data.shortLabel, longLabel: data.longLabel };
      });
    } else if (Array.isArray(enumsQuerySnap?.docs)) {
      for (const d of enumsQuerySnap.docs) {
        const data = d.data ? d.data() : d;
        const id = d.id ?? data?.id;
        if (!id) continue;
        decimalEnums[id] = { id, value: data.value, shortLabel: data.shortLabel, longLabel: data.longLabel };
      }
    }
  } catch (enumErr) {
    // Intentionally ignore errors; default to empty enum map for deterministic behavior in tests
  }

  const startDate = ensureValidDate(offerData.startDate || new Date());
  const endDate = ensureValidDate(offerData.endDate || new Date());

  const wfRaw = (locationData as any).workflowStatus;
  const wf = typeof wfRaw === "string" && wfRaw !== "" ? wfRaw : null;
  const forceHideForOffer =
    wf != null ? shouldForceHideFromWorkflowStatus(wf) : (locationData as any).forceHide === true;

  // KB-3733 — what this save WRITES; the session's observed facts come from `storedOffer` (pre-edit).
  const write: RebuildWrite = {
    startDate,
    endDate,
    numDaysOverride: offerData.numDaysOverride ?? null,
    priceOverride: offerData.priceOverride ?? null,
    currentSalePrice: (offerData as any).currentSalePrice ?? null,
    pricingTierPriceOverrides: (offerData as any).pricingTierPriceOverrides ?? null,
  };

  const catalogCore = prepareOfferDataCore({
    location: {
      id: locationId,
      name: locationData.name || locationId,
      nameSubtitle: (locationData as any).nameSubtitle,
      address: locationData.address,
      orgName: resolvedOrgName,
      searchResultExcerpt: locationData.searchResultExcerpt,
      photos: (locationData as any).photos,
      campExperiences: (locationData as any).campExperiences,
      providedByOrg: locationData.providedByOrg as any,
      religiousAffiliationKeys: locationData.religiousAffiliationKeys,
      allergyKeys: (locationData as any).allergyKeys,
      visibility: locationData.visibility,
      forceHide: forceHideForOffer,
      doesScholarships: (locationData as any).doesScholarships,
      doesWaitlist: (locationData as any).doesWaitlist,
      coedKey: (locationData as any).coedKey,
      descAboutTheOrg: (locationData as any).descAboutTheOrg,
      descLocation: (locationData as any).descLocation,
      descTimingAndBooking: (locationData as any).descTimingAndBooking,
      supportForKeys: (locationData as any).supportForKeys
    },
    variation: variation as any,
    decimalEnums,
    form: {
      displayTitle: offerData.displayTitle,
      description: offerData.description,
      eligibilityNotes: offerData.eligibilityNotes,
      url: offerData.url,
      regUrl: offerData.regUrl,
      availabilityStatus: offerData.availabilityStatus,
      signupStatus: offerData.signupStatus,
      startDate,
      endDate,
      numDaysOverride: offerData.numDaysOverride ?? null,
      // KB-2877 (review round 1, B3) — the client seam must forward the withhold too, or the
      // branch in prepareOfferDataCore has no producer on this path and a re-save resolves a
      // withheld offer back to `variation.numDays`. Same pattern as KB-2103's `priceState`
      // feedback a few lines below: feed the CURATED state back in so a deliberate refusal
      // survives a re-save rather than being silently recomputed away.
      numDaysUnknown: (offerData as any).numDaysState === "withheld",
      myOfferIncrementKey: offerData.myOfferIncrementKey ?? null,
      priceOverride: offerData.priceOverride ?? null as any,
      currentSalePrice: (offerData as any).currentSalePrice ?? null,
      // KB-2103 — feed the CURATED state back in so a human's "free" survives a
      // re-save. Without it every save would recompute the offer to "unknown"
      // and silently erase the curation on the next edit.
      priceState: (offerData as any).priceState ?? null,
      regEvents: offerData.regEvents ?? null,
      agesAreOverrides: (offerData as any).agesAreOverrides === true,
      variationAgeOverrideInput: (offerData as any).variationAgeOverrideInput,
      pricingTierPriceOverrides: (offerData as any).pricingTierPriceOverrides ?? null,
      // KB-4516 — the core skips the default drop-off/pick-up window on a placeholder.
      isPlaceholder: offerData.isPlaceholder === true,
      ...campDataCatalogInputs(storedOffer, write),
      // KB-4319 — and its own age/grade band, so the rebuild does not reset it to the program's.
      // `offerData` is the stored doc with this save laid over it, so an untick or an explicit override in the session
      // dialog is seen (review round 1 F2).
      ...(isCampDataOffer(storedOffer) ? storedSessionAgeInputs(storedOffer as Record<string, unknown>, offerData as any) : {}),
    }
  });
  // KB-4564 — and keeps the drop-off/pick-up window the stored offer holds, which the catalog pass rebuilt.
  const core = applyStoredSessionWindow(applyStoredSessionPrice(catalogCore, storedOffer, variation as any, write), storedOffer, {
    isPlaceholder: offerData.isPlaceholder === true,
    variationKey: offerData.variationKey,
  });

  // Apply age/grade overrides into variationData when enabled
  const finalVariationData = { ...core.sanitizedVariationData } as any;
  // Tier-2/tier-3 overrides are session-level: stored on offer as pricingTierPriceOverrides.tier2/.tier3 (set in completeOfferData below)
  if ((offerData as any).agesAreOverrides === true && (offerData as any).variationAgeOverrideInput) {
    const o: any = (offerData as any).variationAgeOverrideInput;
    if (o.minAgeInYears != null) finalVariationData.minAgeInYears = o.minAgeInYears;
    if (o.maxAgeInYears != null) finalVariationData.maxAgeInYears = o.maxAgeInYears;
    if (o.minAgeGradeKey != null) finalVariationData.minAgeGradeKey = o.minAgeGradeKey;
    if (o.maxAgeGradeKey != null) finalVariationData.maxAgeGradeKey = o.maxAgeGradeKey;
    if (o.minAgeNote != null) finalVariationData.minAgeNote = o.minAgeNote;
    if (o.maxAgeNote != null) finalVariationData.maxAgeNote = o.maxAgeNote;
    if (o.showMinAgeGradeAsPrimary != null) finalVariationData.showMinAgeGradeAsPrimary = o.showMinAgeGradeAsPrimary;
    if (o.showMaxAgeGradeAsPrimary != null) finalVariationData.showMaxAgeGradeAsPrimary = o.showMaxAgeGradeAsPrimary;
    
    // Compute grade values from grade keys when applying overrides
    if (o.minAgeGradeKey && decimalEnums[o.minAgeGradeKey]) {
      const gradeEnum = decimalEnums[o.minAgeGradeKey];
      finalVariationData.minAgeGradeValue = gradeEnum.value !== undefined ? gradeEnum.value : null;
    } else if (o.minAgeGradeKey == null) {
      finalVariationData.minAgeGradeValue = null;
    }
    
    if (o.maxAgeGradeKey && decimalEnums[o.maxAgeGradeKey]) {
      const gradeEnum = decimalEnums[o.maxAgeGradeKey];
      finalVariationData.maxAgeGradeValue = gradeEnum.value !== undefined ? gradeEnum.value : null;
    } else if (o.maxAgeGradeKey == null) {
      finalVariationData.maxAgeGradeValue = null;
    }
  }

  // Backfill grade values when not overriding but keys exist and values are missing
  if (finalVariationData.minAgeGradeValue == null && finalVariationData.minAgeGradeKey && decimalEnums[finalVariationData.minAgeGradeKey]) {
    finalVariationData.minAgeGradeValue = decimalEnums[finalVariationData.minAgeGradeKey].value;
  }
  if (finalVariationData.maxAgeGradeValue == null && finalVariationData.maxAgeGradeKey && decimalEnums[finalVariationData.maxAgeGradeKey]) {
    finalVariationData.maxAgeGradeValue = decimalEnums[finalVariationData.maxAgeGradeKey].value;
  }

  // Compute effective age fields: use ageInYears if defined, otherwise gradeValue + 5 (or +4 if -1)
  const fromGrade = (val: number | null | undefined) =>
    (typeof val === 'number' ? val + (val === -1 ? 4 : 5) : undefined);

  if (finalVariationData.maxAgeInYears != null && finalVariationData.maxAgeInYears !== undefined) {
    finalVariationData.maxAgeEffective = finalVariationData.maxAgeInYears;
  } else {
    const maxFromGrade = fromGrade(finalVariationData.maxAgeGradeValue);
    if (maxFromGrade !== undefined) finalVariationData.maxAgeEffective = maxFromGrade;
  }
  
  if (finalVariationData.minAgeInYears != null && finalVariationData.minAgeInYears !== undefined) {
    finalVariationData.minAgeEffective = finalVariationData.minAgeInYears;
  } else {
    const minFromGrade = fromGrade(finalVariationData.minAgeGradeValue);
    if (minFromGrade !== undefined) finalVariationData.minAgeEffective = minFromGrade;
  }

  // KB-4319 — on a camp-data offer, not adding a sentinel the stored `variationData` lacks.
  normalizeAgeUiSentinelsKeepingStoredShape(
    finalVariationData,
    normalizeVariationDataAgeUiSentinels,
    isCampDataOffer(storedOffer) ? (storedOffer as Record<string, unknown>) : null,
  );

  const userRef = doc(db, "users", userId);

  const slugPath = await resolveCampSlugPathForOffer(db, locationId, locationData);

  // KB-2143 — hoisted out of the payload literal so the DROP can be logged, mirroring the server
  // seam. Keeping only `.regUrl` computed the diagnosis and discarded it, so a dropped registration
  // link looked exactly like an offer that never had one: the Register button just quietly stops
  // rendering for parents and nothing records why.
  const regUrlSanitized = sanitizeAuthoredRegUrl(offerData.regUrl ?? null, {
    seedUrl: (variation as any)?.dataFetchUrl ?? null,
  });
  if (regUrlSanitized.dropped) {
    console.warn("[regUrl-drop] refused to persist a regUrl at the client offer-prep seam", {
      reason: regUrlSanitized.reason,
      regUrl: offerData.regUrl ?? null,
      locationId,
      variationKey: offerData.variationKey,
      seedUrl: (variation as any)?.dataFetchUrl ?? null,
    });
  }

  // KB-2782 AC-2 — mirrors the server seam's [price-tier-withheld] log. core.priceTier2Calc/
  // priceTier3Calc below are already withheld (null) when they weren't strictly below the public
  // price; without this the client save path was the one seam where the refusal rendered as
  // nothing at all.
  if (core.priceTierWithheld) {
    console.warn("[price-tier-withheld] refused to persist a tier price at or above the public price", {
      ...core.priceTierWithheld,
      locationId,
      variationKey: offerData.variationKey,
    });
  }

  const completeOfferData = {
    _geoloc: (locationData as any)._geoloc || null,
    availabilityStatus: offerData.availabilityStatus || 'av_unknown',
    campExperienceData: (locationData as any).campExperiences && (locationData as any).campExperiences[0] ? { ...(locationData as any).campExperiences[0] } : {},
    createdAt: offerData.createdAt || serverTimestamp(),
    createdBy: userRef as unknown as DocumentReference,
    // Preserve avStatusAsOf on edits; creator sets it separately on create
    avStatusAsOf: (offerData as any).avStatusAsOf,
    // Preserve firstAvailableAt so saves don’t null it out inadvertently
    firstAvailableAt: (offerData as any).firstAvailableAt ?? null,
    displayTitle: offerData.displayTitle || "",
    description: offerData.description || "",
    eligibilityNotes: offerData.eligibilityNotes || "",
    endDate: endDate,
    lastEditedAt: serverTimestamp(),
    lastEditedBy: userRef as unknown as DocumentReference,
    locationData: { ...core.locationData, slugPath },
    locationRef: locationRef,
    numDaysCalc: core.numDaysCalc,
    // KB-2877 (review round 2, O4) — this whitelist is why the admin side could never CREATE the
    // withheld state: it rebuilds `numDaysCalc`/`pricePerDayCalc` from `core` and had no
    // `numDaysState` key at all, so a copied withheld offer was born `numDaysCalc: null` with no
    // reason attached and reverted to the variation default on its next publish. Deriving it from
    // `core` rather than echoing the input keeps it coupled to the value it describes — the same
    // reason `computeOfferCoreFields` derives it beside `numDaysCalc` (data-model §1d).
    numDaysState: (core as any).numDaysState ?? null,
    numDaysOverride: offerData.numDaysOverride,
    myOfferIncrementKey: offerData.myOfferIncrementKey || variation.regularOfferIncrementKey || null,
    priceCalc: core.priceCalc,
    pricePerDayCalc: core.pricePerDayCalc,
    // KB-1412 — histogram bin floors faceted by Algolia (priceBucket /
    // pricePerDayBucket); null for no-price offers → absent from the facet.
    priceBucket: core.priceBucket,
    pricePerDayBucket: core.pricePerDayBucket,
    priceRegular: core.priceRegular,
    // KB-2103 — priced (null) / "free" / "unknown". Without this line the
    // deriver would compute correctly and the value would never reach the
    // offer doc, so the Algolia price filters could not exclude unknown.
    priceState: core.priceState,
    priceTier2Calc: core.priceTier2Calc,
    priceTier3Calc: core.priceTier3Calc,
    priceOverride: offerData.priceOverride,
    priceNoteOverride: (offerData as any).priceNoteOverride ?? null,
    currentSalePrice: (offerData as any).currentSalePrice ?? null,
    pricingTierPriceOverrides: (offerData as any).pricingTierPriceOverrides ?? null,
    signupStatus: offerData.signupStatus || 'su_unknown',
    startDate: startDate,
    timingNoteOverride: (offerData as any).timingNoteOverride ?? null,
    variationData: finalVariationData,
    variationKey: offerData.variationKey || "",
    // KB-2383 — derive from the parent location rather than hardcoding, mirroring the pipeline
    // writer's deriveOfferVisibilityFromLocation. A hardcoded "published" here let a draft/
    // unpublished location's offer land publicly searchable via the wholesale Algolia mirror.
    //
    // KB-3641 — and hold a ROLLED-BACK offer at draft. `updateOffer` (`offerCrud.ts`) loads the
    // stored doc and passes `{ ...currentOffer, ...offerData }` through here, so `offerData` carries
    // `rollbackUnpublishedAt` for an offer an operator un-created. Without this, editing an
    // unrelated field — a description — recomputes `visibility` from the still-`v_published` parent
    // and republishes it into Algolia within seconds. Worse, `updateDoc` is a partial write and this
    // payload never emits the marker, so the marker SURVIVES: a later rollback run then reports
    // `alreadyUnpublished` over a live, indexed offer and can never re-unpublish it.
    // `offerBatchUpdateService` shares this seam and is fixed by the same line.
    visibility: deriveOfferVisibility((locationData as any)?.visibility, {
      rollbackUnpublished: (offerData as any)?.rollbackUnpublishedAt != null,
    }),
    url: offerData.url || null,
    // KB-2134 — mirror the admin/server seam: refuse to persist a vendor LISTING / scrape-SEED URL as
    // a per-session regUrl. `|| null` preserves the prior empty-string → null behavior; a valid deep
    // link / checkout link is byte-unchanged.
    regUrl: regUrlSanitized.regUrl || null,
    regEvents: offerData.regEvents || null,
    isPlaceholder: offerData.isPlaceholder ?? false,
    agesAreOverrides: (offerData as any).agesAreOverrides === true,
    numPlaceholderOffers: (typeof (locationData as any).placeholderOffersCount === 'number') ? (locationData as any).placeholderOffersCount : null,
    // Optional idempotency key for bulk imports (org-mode RowFormat, camp-data ingestion helpers, etc.)
    sourceKey: (offerData as any).sourceKey ?? null
  } as any;

  Object.entries(completeOfferData).forEach(([k, v]) => { if (v === undefined) (completeOfferData as any)[k] = null; });

  // KB-4351 — mirrors the server seam: copy the program's acceptable `programUrl`; absent stays absent.
  // KB-4346 — and clear the program's own copy when the program's is cleared (`programUrlSource`).
  const programUrl = offerProgramUrlFields(variation as any, storedOffer);
  if (programUrl.refused) {
    console.warn("[programUrl-drop] refused to copy a program's programUrl onto the offer", {
      reason: programUrl.refused,
      programUrl: programUrl.candidate,
      locationId,
      variationKey: offerData.variationKey,
    });
  }
  Object.assign(completeOfferData, programUrl.fields);

  return completeOfferData;
};
